import { useState } from 'react';

import { useSettingsStore, type GameSettings, type TextScale } from '../state/settings-store';
import styles from './LastWreckScreen.module.css';
import { Icon } from './icons';

interface PauseAndSettingsProps {
  open: boolean;
  onClose: () => void;
  onRestart: () => void;
}

function VolumeSlider({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: number;
  disabled: boolean;
  onChange: (value: number) => void;
}) {
  return (
    <label className={styles.sliderRow}>
      <span>{label}</span>
      <input
        aria-valuetext={`${String(Math.round(value * 100))} percent`}
        disabled={disabled}
        max="100"
        min="0"
        onChange={(event) => {
          onChange(Number(event.currentTarget.value) / 100);
        }}
        type="range"
        value={Math.round(value * 100)}
      />
      <output>{Math.round(value * 100)}</output>
    </label>
  );
}

function Toggle({
  checked,
  description,
  label,
  onChange,
}: {
  checked: boolean;
  description: string;
  label: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className={styles.toggleRow}>
      <span>
        <strong>{label}</strong>
        <small>{description}</small>
      </span>
      <input
        checked={checked}
        onChange={(event) => {
          onChange(event.currentTarget.checked);
        }}
        type="checkbox"
      />
      <span aria-hidden="true" className={styles.toggleTrack} />
    </label>
  );
}

export function PauseAndSettings({ open, onClose, onRestart }: PauseAndSettingsProps) {
  const settings = useSettingsStore((state) => state.settings);
  const updateSettings = useSettingsStore((state) => state.updateSettings);
  const resetTutorial = useSettingsStore((state) => state.resetTutorial);
  const resetSave = useSettingsStore((state) => state.resetSave);
  const [confirmAction, setConfirmAction] = useState<'restart' | 'save' | null>(null);

  const update = <Key extends keyof GameSettings>(key: Key, value: GameSettings[Key]) => {
    updateSettings({ [key]: value });
  };

  if (!open) return null;

  return (
    <div
      aria-labelledby="pause-title"
      aria-modal="true"
      className={styles.modalScrim}
      role="dialog"
    >
      <section className={styles.pausePanel}>
        <header className={styles.panelHeader}>
          <div>
            <span className={styles.eyebrow}>SYSTEM HOLD</span>
            <h2 id="pause-title">Drone controls paused</h2>
          </div>
          <button
            aria-label="Resume salvage operation"
            className={styles.iconButton}
            onClick={onClose}
          >
            <Icon name="close" />
          </button>
        </header>

        <div className={styles.settingsGrid}>
          <section aria-labelledby="audio-settings">
            <h3 id="audio-settings">Audio channels</h3>
            <Toggle
              checked={!settings.muted}
              description="Persisted immediately"
              label="Audio enabled"
              onChange={(checked) => {
                update('muted', !checked);
              }}
            />
            <VolumeSlider
              disabled={settings.muted}
              label="Master"
              onChange={(value) => {
                update('masterVolume', value);
              }}
              value={settings.masterVolume}
            />
            <VolumeSlider
              disabled={settings.muted}
              label="Music"
              onChange={(value) => {
                update('musicVolume', value);
              }}
              value={settings.musicVolume}
            />
            <VolumeSlider
              disabled={settings.muted}
              label="Effects"
              onChange={(value) => {
                update('effectsVolume', value);
              }}
              value={settings.effectsVolume}
            />
          </section>

          <section aria-labelledby="display-settings">
            <h3 id="display-settings">Display & motion</h3>
            <Toggle
              checked={settings.reducedMotion}
              description="Replaces sweeping and camera motion"
              label="Reduced motion"
              onChange={(checked) => {
                update('reducedMotion', checked);
              }}
            />
            <Toggle
              checked={settings.screenShake}
              description="Impact and structural event response"
              label="Screen shake"
              onChange={(checked) => {
                update('screenShake', checked);
              }}
            />
            <Toggle
              checked={settings.highContrast}
              description="Brighter outlines and stronger patterns"
              label="High contrast interface"
              onChange={(checked) => {
                update('highContrast', checked);
              }}
            />
            <Toggle
              checked={settings.oneHandedMode}
              description="Enlarges mobile tool buttons"
              label="One-handed controls"
              onChange={(checked) => {
                update('oneHandedMode', checked);
              }}
            />
            <fieldset className={styles.segmentedField}>
              <legend>Text size</legend>
              {[100, 115, 130].map((scale) => (
                <button
                  aria-pressed={settings.textScale === scale}
                  key={scale}
                  onClick={() => {
                    update('textScale', scale as TextScale);
                  }}
                  type="button"
                >
                  {scale}%
                </button>
              ))}
            </fieldset>
          </section>
        </div>

        <footer className={styles.pauseFooter}>
          <button className={styles.quietButton} onClick={resetTutorial} type="button">
            Reset tutorial
          </button>
          <button
            className={styles.quietButton}
            onClick={() => {
              setConfirmAction('save');
            }}
            type="button"
          >
            Reset records
          </button>
          <button
            className={styles.dangerButton}
            onClick={() => {
              setConfirmAction('restart');
            }}
            type="button"
          >
            Restart run
          </button>
          <button className={styles.primaryButton} onClick={onClose} type="button">
            Resume operation
          </button>
        </footer>

        {confirmAction !== null && (
          <div className={styles.confirmStrip} role="alertdialog">
            <p>
              {confirmAction === 'restart'
                ? 'Abandon this run and return to the docking approach?'
                : 'Clear best score, successful extractions, and settings?'}
            </p>
            <button
              className={styles.quietButton}
              onClick={() => {
                setConfirmAction(null);
              }}
            >
              Keep current data
            </button>
            <button
              className={styles.dangerButton}
              onClick={() => {
                if (confirmAction === 'restart') onRestart();
                else resetSave();
                setConfirmAction(null);
              }}
            >
              Confirm
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
